<template>
    <CieloLayout>
        <div class="edit-customer view-container">
            <div class="edit-customer__container spacing-b-6">
                <TitleText class="spacing-b-6" level="2">
                    {{ $ml.get('editCustomerTitle') }}
                </TitleText>

                <CieloInput
                    class="personal-data-input"
                    :autofocus="false"
                    :borderless="false"
                    name="Email"
                    :label="$ml.get('email')"
                    v-model="localCustomer.email"
                    v-validate="'required|safe-mail'"
                    :data-vv-as="$ml.get('email')"
                    type="email"
                    :error="errors.first('Email')"
                    :disabled="true"
                />

                <CieloInput
                    :autofocus="true"
                    :borderless="false"
                    name="Name"
                    :label="$ml.get('fullName')"
                    v-model="localCustomer.fullName"
                    v-validate="'required|fullname'"
                    :data-vv-as="$ml.get('fullName')"
                    type="text"
                    :error="errors.first('Name')"
                    class="personal-data-input"
                />

                <CieloInput
                    :autofocus="false"
                    :borderless="false"
                    name="Phone"
                    :label="$ml.get('cellPhone')"
                    v-model="localCustomer.phone"
                    v-validate="'required|numeric|min:11'"
                    :data-vv-as="$ml.get('cellPhone')"
                    type="tel"
                    :error="errors.first('Phone')"
                    mask="(##) #####-####"
                />

                <div class="edit-customer__wrapper--identity">
                    <CieloSelect
                        @change="selectDocumentType"
                        :title="$ml.get('documentType')"
                        :label="$ml.get('selectDocumentType')"
                        :option-items="documentTypes"
                        :selected-item="localCustomer.identityType"
                        other-class="cielo-select__container--documentType"
                        :can-translate="true"
                    />
                    <CieloInput
                        style="width: 100%; margin-bottom: 0px"
                        :autofocus="false"
                        :borderless="false"
                        name="Identity"
                        :label="$ml.get('identity')"
                        v-model="localCustomer.identity"
                        v-validate="identityValidationRule"
                        :data-vv-as="$ml.get('identity')"
                        :type="identityInputType"
                        :error="errors.first('Identity')"
                        :mask="identityMask"
                    />
                </div>
            </div>

            <div class="edit-customer__cta-container">
                <ButtonsNextPrevious
                    :disable-button-next="disableButtonSaveAndContinue"
                    :loading="loading"
                    :back-text="'cancel'"
                    :next-text="'saveAndContinue'"
                    @click-next="saveAndContinue"
                    @click-previous="goToPreviousStep"
                />
            </div>
        </div>
    </CieloLayout>
</template>

<script>
	import { mapGetters } from 'vuex';

	import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';
	import FullNameValidation from '@/helpers/validation-rules/FullNameValidation';
	import SafeMailValidation from '@/helpers/validation-rules/SafeMailValidation';
    import { logEvent } from '@/helpers/cieloLogger';

	const CieloInput = () =>
		import(
            /* webpackChunkName: "cielo-input" */ '@/components/template/CieloInput'
		);
    import CieloLayout from '@/components/layout/CieloLayout';
	import TitleText from '@/components/template/TitleText';
    import CieloSelect from '@/components/template/CieloSelect/CieloSelect';
    import ButtonsNextPrevious from '@/components/template/ButtonsNextPrevious';

	export default {
		components: {
			CieloInput,
			TitleText,
			CieloSelect,
            ButtonsNextPrevious,
            CieloLayout
		},

		data() {
			return {
				localCustomer: {},
				documentTypes: [
					{ Text: 'cpf', Value: 'CPF' },
					{ Text: 'cnpj', Value: 'CNPJ' },
					{ Text: 'foreign', Value: 'FOREIGN' },
				],
                loading: false,
			};
		},

		created() {
			this.localCustomer = JSON.parse(JSON.stringify(this.customer));
		},

		mounted() {
			this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
			this.$validator.extend('fullname', FullNameValidation);
			this.$validator.extend('safe-mail', SafeMailValidation);
		},

		computed: {
			...mapGetters({
				customer: 'dataOnFile/getCustomer',
			}),

            identityValidationRule() {
				switch (this.localCustomer.identityType) {
					case 'CPF':
						return 'required|numeric|min:11|max:11|cpf-cnpj';
					case 'CNPJ':
						return 'required|min:14|max:18|cpf-cnpj';
					default:
						return 'required|max:32';
				}
			},
			identityMask() {
				switch (this.localCustomer.identityType) {
					case 'CPF':
						return ['###.###.###-##'];
					case 'CNPJ':
						return ['XX.XXX.XXX/XXXX-XX'];
					default:
						return null;
				}
			},
			identityInputType() {
				return ['CPF', 'CNPJ'].includes(this.localCustomer.identityType) ? 'tel' : 'text';
			},

            disableButtonSaveAndContinue() {
                return false;
            },
		},

		methods: {
			selectDocumentType(value) {
				this.localCustomer.identityType = value;
			},

            goToPreviousStep() {
                const backRoute = this.$store.getters['dataOnFile/getEditCustomerBackRoute'] || 'DataOnFileReview';

                if (backRoute === 'ChangePaymentType') {
                    this.$store.commit('dataOnFile/setSelectedCardId', null);
                    this.$store.commit('dataOnFile/setSelectedBillingAddressId', null);
                }

                this.$store.dispatch('pipeline/pushTo', backRoute);
            },

            async saveAndContinue() {
                if(this.loading) return;
                this.loading = true;

                let isValid = await this.$validator.validateAll();

                if(!isValid) {
                    this.loading = false;
                    return;
                }

                try {
                    await this.$store.dispatch('dataOnFile/editCustomer', this.localCustomer);

                    logEvent({
                        category: 'data_on_file',
                        action: 'dof_edicao_dados',
                        label: 'Usuário que já está identificado e tem cadastro no dof, na tela de revsãoclica em editar os dados pessoais, atualiza eles e salva'
                    });

                    this.$store.dispatch('pipeline/pushTo', 'DataOnFileReview');
                } catch (error) {
                    // TODO: tratar o erro na view?
                    console.error(error);
                } finally {
                    this.loading = false;
                }
            },
		},
	};
</script>

<style scoped lang="scss">
    .edit-customer {
        &__wrapper--identity {
            display: flex;
			gap: 20px;
			width: 100%;

			@media screen and (max-width: 1024px) {
				flex-direction: column;
				margin-top: 0px;
			}
        }
    }
</style>
